import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { ArrowRight, Award, Briefcase, GraduationCap, TrendingUp } from 'lucide-react';
import { profileData, stats, skills } from '../mock/mockData';

const Home = () => {
  return (
    <div className="relative z-10">
      {/* Hero Section */}
      <section className="min-h-screen flex items-center justify-center pt-20 pb-12 px-6">
        <div className="container mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left Content */}
            <div className="space-y-6">
              <Badge className="bg-blue-900/30 text-sky-300 hover:bg-blue-900/40 border-0">
                Senior Executive Leader
              </Badge>
              <h1 className="text-5xl lg:text-6xl font-bold leading-tight">
                <span className="text-white">{profileData.name}</span>
              </h1>
              <p className="text-xl text-sky-300 font-semibold">
                {profileData.title}
              </p>
              <p className="text-lg text-gray-300 leading-relaxed">
                {profileData.tagline}
              </p>
              <div className="space-y-2">
                <div className="flex items-center space-x-2">
                  <Award className="h-5 w-5 text-sky-400" />
                  <span className="text-gray-200">Honorary Professor at Amity University Dubai</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Briefcase className="h-5 w-5 text-blue-400" />
                  <span className="text-gray-200">Serial Entrepreneur & Senior Director with 15 Years of Experience in the GCC</span>
                </div>
                <div className="flex items-center space-x-2">
                  <TrendingUp className="h-5 w-5 text-sky-400" />
                  <span className="text-gray-200">Founder of Dubai Solar Schools Programme</span>
                </div>
              </div>
              <div className="flex flex-wrap gap-4 pt-4">
                <Link to="/contact">
                  <Button className="bg-gradient-to-r from-blue-600 to-sky-500 hover:from-blue-700 hover:to-sky-600 text-white">
                    Get in Touch <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
                <Link to="/experience">
                  <Button variant="outline" className="border-sky-400 text-sky-300 hover:bg-blue-900/30">
                    View Experience
                  </Button>
                </Link>
              </div>
            </div>

            {/* Right Content - Image */}
            <div className="flex justify-center lg:justify-end">
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-br from-blue-600 to-sky-500 rounded-2xl blur-2xl opacity-20"></div>
                <img
                  src={profileData.photo}
                  alt={profileData.name}
                  className="relative rounded-2xl shadow-2xl w-full max-w-md object-cover ring-2 ring-sky-500/30"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 px-6 bg-gradient-to-br from-blue-950/20 to-sky-950/20">
        <div className="container mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {stats.map((stat, index) => (
              <Card key={index} className="p-6 text-center border-blue-900/30 shadow-lg bg-[#060f1e]/80 backdrop-blur hover:shadow-xl hover:shadow-blue-500/10 transition-shadow">
                <div className="text-4xl font-bold bg-gradient-to-r from-sky-400 to-blue-400 bg-clip-text text-transparent">
                  {stat.value}
                </div>
                <div className="text-sm text-gray-300 mt-2">{stat.label}</div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="py-20 px-6">
        <div className="container mx-auto max-w-4xl">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-white mb-4">Professional Overview</h2>
            <div className="h-1 w-24 bg-gradient-to-r from-blue-500 to-sky-400 mx-auto rounded-full"></div>
          </div>
          <Card className="p-8 border-blue-900/30 shadow-xl bg-[#060f1e]/80 backdrop-blur">
            <p className="text-lg text-gray-200 leading-relaxed mb-6">
              {profileData.summary}
            </p>
            <div className="grid md:grid-cols-2 gap-6 mt-8">
              <div>
                <h3 className="text-xl font-semibold text-white mb-4 flex items-center">
                  <GraduationCap className="h-6 w-6 text-sky-400 mr-2" />
                  Education
                </h3>
                <ul className="space-y-2 text-gray-300">
                  <li>• Honorary Professorship - Amity University Dubai</li>
                  <li>• Master in Sustainable Architecture - Politecnico Milan</li>
                  <li>• B.Sc. Cum Laude - University of Florence</li>
                </ul>
              </div>
              <div>
                <h3 className="text-xl font-semibold text-white mb-4 flex items-center">
                  <Award className="h-6 w-6 text-blue-400 mr-2" />
                  Key Achievements
                </h3>
                <ul className="space-y-2 text-gray-300">
                  <li>• Young Global Leader Award 2017</li>
                  <li>• 100+ Keynote Speeches in UAE</li>
                  <li>• 500+ MWp Solar Permitting Approvals</li>
                </ul>
              </div>
            </div>
          </Card>
        </div>
      </section>

      {/* Skills Section */}
      <section className="py-20 px-6 bg-gradient-to-br from-sky-950/20 to-blue-950/20">
        <div className="container mx-auto max-w-5xl">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-white mb-4">Core Competencies</h2>
            <div className="h-1 w-24 bg-gradient-to-r from-blue-500 to-sky-400 mx-auto rounded-full"></div>
          </div>
          <div className="flex flex-wrap gap-3 justify-center">
            {skills.map((skill, index) => (
              <Badge
                key={index}
                className="px-4 py-2 text-sm bg-[#060f1e]/80 text-gray-200 border border-blue-900/30 hover:bg-blue-900/30 hover:border-blue-700 transition-all cursor-default"
              >
                {skill}
              </Badge>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6">
        <div className="container mx-auto max-w-4xl">
          <Card className="p-12 border-0 shadow-2xl bg-gradient-to-br from-blue-600 to-sky-500 text-white text-center">
            <h2 className="text-3xl font-bold mb-4">Let's Connect</h2>
            <p className="text-lg mb-8 text-blue-100">
              Interested in collaboration or consultation? I'd love to hear from you.
            </p>
            <Link to="/contact">
              <Button className="bg-white text-blue-600 hover:bg-gray-100">
                Contact Me <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </Card>
        </div>
      </section>
    </div>
  );
};

export default Home;
